import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { ApiError, RANK_LABEL, api, type AuthUser } from '@/lib/api';

export default function Account({ user }: { user: AuthUser }) {
  const linkQuery = useQuery({
    queryKey: ['my-player-link'],
    queryFn: () => api.myPlayerLink(),
    enabled: user.playerId != null,
    staleTime: 5 * 60 * 1000,
  });
  const token = linkQuery.data?.token ?? null;

  return (
    <div className="mx-auto w-full max-w-lg space-y-6">
      <header>
        <h1 className="text-xl font-semibold">Account</h1>
        <p className="text-sm text-slate-400">Manage your sign-in credentials.</p>
      </header>

      <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h2 className="text-sm font-medium uppercase tracking-wider text-slate-400">
          Profile
        </h2>
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-slate-500">Username</dt>
          <dd className="font-medium">{user.username}</dd>
          <dt className="text-slate-500">Role</dt>
          <dd>{RANK_LABEL[user.role]}</dd>
          {user.playerId != null && (
            <>
              <dt className="text-slate-500">My hub</dt>
              <dd>
                {token ? (
                  <a
                    href={`/players/${encodeURIComponent(token)}/self-report`}
                    className="inline-flex items-center gap-1 text-indigo-300 hover:text-indigo-200"
                  >
                    Open my player page
                    <ExternalLink size={12} />
                  </a>
                ) : (
                  <span className="text-slate-500">
                    {linkQuery.isLoading ? 'Loading…' : 'No magic link on file'}
                  </span>
                )}
              </dd>
            </>
          )}
        </dl>
      </section>

      <ChangePasswordCard />
    </div>
  );
}

function ChangePasswordCard() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: () => api.changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      setSuccess(true);
      setError(null);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (err) => {
      setSuccess(false);
      if (err instanceof ApiError) {
        setError(err.status === 401 ? 'Current password is incorrect' : err.message);
      } else {
        setError('Failed to change password');
      }
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    mutation.mutate();
  };

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <h2 className="text-sm font-medium uppercase tracking-wider text-slate-400">
        Change password
      </h2>
      <form onSubmit={submit} className="mt-3 space-y-3">
        <Field label="Current password">
          <input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            className="input"
          />
        </Field>
        <Field label="New password">
          <input
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
            className="input"
          />
        </Field>
        <Field label="Confirm new password">
          <input
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={8}
            className="input"
          />
        </Field>
        {error && <div className="text-sm text-red-400">{error}</div>}
        {success && <div className="text-sm text-emerald-400">Password updated.</div>}
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded bg-indigo-600 px-4 py-2 text-sm hover:bg-indigo-500 disabled:opacity-50"
        >
          {mutation.isPending ? 'Saving…' : 'Update password'}
        </button>
      </form>
      <Style />
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="text-slate-300">{label}</span>
      {children}
    </label>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        display: block;
        width: 100%;
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 8px 10px;
        font-size: 14px;
        color: inherit;
      }
      .input:focus { outline: none; border-color: rgb(100 116 139 / 1); }
    `}</style>
  );
}
